import { computed } from 'vue'
import { useQuasar } from 'quasar'
import quasarLanguages from 'quasar/lang/index.json'
import { useI18n } from 'vue-i18n'
import {
  LOCALE_SERVER_INDEX,
  LOCALE_FLAGS,
  SUPPORTED_LOCALES,
  flagForLocale,
  nativeNameFor,
  type MenuLanguageOption,
} from 'src/i18n/supported-locales'

// ⚠️ This file used to carry its OWN hardcoded 14-language allow-list and its own
// 6-entry flag map — private duplicates of supported-locales.ts. Nobody updated them as
// locales were added, so the picker silently stopped at 14 while the app shipped 24
// (sk/hi/hu/nl/nb-NO/ro/sv/th/vi/ja were all translated but unreachable in the UI).
// Both duplicates are gone: SUPPORTED_LOCALES and LOCALE_FLAGS are the single source.
// Do not reintroduce a local list here — add the locale to supported-locales.ts instead.

export function useLanguage() {
  const { locale } = useI18n({ useScope: 'global' })
  const $q = useQuasar()

  // Exactly the locales in SUPPORTED_LOCALES, in Quasar's (alphabetical) order, with
  // Quasar's occasionally-wrong nativeName corrected. SUPPORTED_LOCALES holds full Quasar
  // isoNames ('zh-CN', 'nb-NO', 'en-US'), so this is a straight membership test.
  const langOptions = computed(() =>
    quasarLanguages
      .filter((lang) =>
        SUPPORTED_LOCALES.includes(lang.isoName as (typeof SUPPORTED_LOCALES)[number]),
      )
      .map((lang) => ({
        ...lang,
        nativeName: nativeNameFor(lang.isoName, lang.nativeName),
      })),
  )

  // The picker list (LanguageSelectField shape): the planner's UI languages that
  // have a server numeral index, sorted by index. Shared by signup + edit profile.
  const languageOptions = computed<MenuLanguageOption[]>(() =>
    langOptions.value
      .map((l) => ({
        index: LOCALE_SERVER_INDEX[l.isoName],
        code: l.isoName,
        name: l.nativeName,
        flag: LOCALE_FLAGS[l.isoName] || 'us',
      }))
      .filter((o) => typeof o.index === 'number')
      .sort((a, b) => a.index - b.index)
  )

  // Callers pass a BASE code (LangSelect uses isoName.split('-')[0]), so resolve both
  // shapes. Returning '' here makes the caller fall back to the raw code as a flag class,
  // which is wrong for any language whose code ≠ its country: 'sv' would render El
  // Salvador, not Sweden. flagForLocale covers every SUPPORTED_LOCALES entry.
  const getFlagByLang = (lang: string) => flagForLocale(lang)

  async function setLocale (payload: {
    isoName: string;
    nativeName: string;
}) {
    const langList = import.meta.glob('../../node_modules/quasar/lang/*.js')
    try {
      langList[ `../../node_modules/quasar/lang/${payload.isoName}.js` ]().then(result => {
        $q.lang.set(result.default)
        locale.value = payload.isoName
      })
    }
    catch (err) {
      console.log("[quasar langpack error]:", err)
    }
  }

  return {
    langOptions,
    languageOptions,
    getFlagByLang,
    setLocale
  }
}
